import { createStore, Event } from 'effector';

import { applyReducers } from '@m/effector-utils';
import { logout } from 'apps/customer/state/user';

import {
  showSideMenu,
  hideSideMenu,
  toggleSideMenu,
  showLastVisitModal,
  hideLastVisitModal,
  showConfirmationModal,
  hideConfirmationModal,
  showHelpModal,
  hideHelpModal,
  showAppModal,
  hideAppModal,
  setIsValetPaymentScreenOpen,
  setIsValetCancelRequestScreenOpen,
  setHideActiveVisitValidationScanner,
  setShowParkingPassReceiptSheet,
} from './actions';

type State = {
  appModalCloseTimeout: number | null;
  appModalContent: React.ReactElement | null;
  appModalStyleOverrides: string | null;
  // Keyed by visit id -- an active-pass purchase on one open visit shouldn't hide the
  // validate button on a sibling open visit's card.
  hideActiveVisitValidationScanner: Record<number, boolean>;
  isAppModalVisible: boolean;
  isConfirmationModalVisible: boolean;
  isHelpModalVisible: boolean;
  isLastVisitModalVisible: boolean;
  isSideMenuVisible: boolean;
  // Keyed by visit id -- an action on one open valet visit shouldn't open the payment/cancel
  // sheet for a sibling open valet visit's card.
  isValetCancelRequestScreenOpen: Record<number, boolean>;
  isValetPaymentScreenOpen: Record<number, boolean>;
  showParkingPassReceipt: boolean;
};

const initialState: State = {
  isSideMenuVisible: false,
  isConfirmationModalVisible: false,
  isLastVisitModalVisible: false,
  isHelpModalVisible: false,
  isAppModalVisible: false,
  appModalContent: null,
  appModalCloseTimeout: null,
  appModalStyleOverrides: null,
  isValetPaymentScreenOpen: {},
  isValetCancelRequestScreenOpen: {},
  hideActiveVisitValidationScanner: {},
  showParkingPassReceipt: false,
};
const store = createStore(initialState);

type Reducers = {
  [actionName: string]: {
    action:
      | Event<void>
      | Event<boolean>
      | Event<{ content: React.ReactElement; timeout?: number }>
      | Event<{ hide: boolean; visitId: number }>
      | Event<{ open: boolean; visitId: number }>
      | Event<Parameters<typeof hideAppModal>[0]>;
    reducer: (state: State, payload: Parameters<typeof showAppModal>[0] & boolean) => State;
  };
};

export const reducers: Reducers = {
  showSideMenu: {
    action: showSideMenu,
    reducer: (state) => ({
      ...state,
      isSideMenuVisible: true,
    }),
  },
  hideSideMenu: {
    action: hideSideMenu,
    reducer: (state) => ({
      ...state,
      isSideMenuVisible: false,
    }),
  },
  toggleSideMenu: {
    action: toggleSideMenu,
    reducer: (state) => ({
      ...state,
      isSideMenuVisible: !state.isSideMenuVisible,
    }),
  },
  showLastVisitModal: {
    action: showLastVisitModal,
    reducer: (state) => ({
      ...state,
      isLastVisitModalVisible: true,
    }),
  },
  hideLastVisitModal: {
    action: hideLastVisitModal,
    reducer: (state) => ({
      ...state,
      isLastVisitModalVisible: false,
    }),
  },
  showConfirmationModal: {
    action: showConfirmationModal,
    reducer: (state) => ({
      ...state,
      isConfirmationModalVisible: true,
    }),
  },
  hideConfirmationModal: {
    action: hideConfirmationModal,
    reducer: (state) => ({
      ...state,
      isConfirmationModalVisible: false,
    }),
  },
  showHelpModal: {
    action: showHelpModal,
    reducer: (state) => ({
      ...state,
      isHelpModalVisible: true,
    }),
  },
  hideHelpModal: {
    action: hideHelpModal,
    reducer: (state) => ({
      ...state,
      isHelpModalVisible: false,
    }),
  },
  showAppModal: {
    action: showAppModal,
    reducer: (state, { content, timeout, styleOverrides }) => ({
      ...state,
      appModalContent: content,
      isAppModalVisible: true,
      appModalCloseTimeout: timeout || null,
      appModalStyleOverrides: styleOverrides || null,
    }),
  },
  hideAppModal: {
    action: hideAppModal,
    reducer: (state) => ({
      ...state,
      // Don't reset appModalContent or appModalCloseTimeout
      isAppModalVisible: false,
      appModalStyleOverrides: null,
    }),
  },
  setIsValetPaymentScreenOpen: {
    action: setIsValetPaymentScreenOpen,
    // Payload typed loosely (see Reducers.reducer) to fit the shared union above -- cast back
    // to this action's real payload shape.
    reducer: (state, payload) => {
      const { open, visitId } = payload as unknown as { open: boolean; visitId: number };
      return {
        ...state,
        isValetPaymentScreenOpen: { ...state.isValetPaymentScreenOpen, [visitId]: open },
      };
    },
  },
  setIsValetCancelRequestScreenOpen: {
    action: setIsValetCancelRequestScreenOpen,
    reducer: (state, payload) => {
      const { open, visitId } = payload as unknown as { open: boolean; visitId: number };
      return {
        ...state,
        isValetCancelRequestScreenOpen: {
          ...state.isValetCancelRequestScreenOpen,
          [visitId]: open,
        },
      };
    },
  },
  setHideActiveVisitValidationScanner: {
    action: setHideActiveVisitValidationScanner,
    // Payload typed loosely (see Reducers.reducer) to fit the shared union above -- cast back
    // to this action's real payload shape.
    reducer: (state, payload) => {
      const { hide, visitId } = payload as unknown as { hide: boolean; visitId: number };
      return {
        ...state,
        hideActiveVisitValidationScanner: {
          ...state.hideActiveVisitValidationScanner,
          [visitId]: hide,
        },
      };
    },
  },
  // TODO: this seems like a temp solution, we should find a better way to handle this
  setShowParkingPassReceiptSheet: {
    action: setShowParkingPassReceiptSheet,
    reducer: (state, showParkingPassReceipt) => ({
      ...state,
      showParkingPassReceipt,
    }),
  },
};

store.reset(logout.done);

export default applyReducers({ storeName: 'customer/ui', store, reducers });
